import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { makePrng } from '@/utils/seededRandom';
import { resolveDayOff } from '@/domain/artistAdmin';
import { holdExpiresAtIso } from '@/domain/depositWindow';
import {
  AWAITING_CLIENT,
  generateDepositLink,
  generateSelfBookLink,
  simulateStripeWebhookPaid,
} from '@/domain/depositFlow';
import { SESSION_DEPOSIT_AUD, SESSION_DURATION_MIN } from '@/domain/pricing';
import type { Artist, Booking, CustomerRecord, Deposit, SelfBookLink, StudioId } from '@/domain/types';

// The reserved held-slot identity lives in depositFlow (avoids an import cycle);
// re-exported here so existing imports from selfBookFlow keep resolving.
export { AWAITING_CLIENT } from '@/domain/depositFlow';

// The universal link supports small / half / full. On the OPEN client-pick flow
// the client still only picks half/full (09-Jul lock); 'small' is reachable only
// when the ARTIST pins it, so the client never chooses a length from a menu.
export type SelfBookSession = 'small' | 'half-day' | 'full-day';

// The two possible start times a client can pick (client-locked, 09 Jul).
export const SELF_BOOK_STARTS = ['11:00', '15:00'] as const;
export type SelfBookStart = (typeof SELF_BOOK_STARTS)[number];

// Slot durations used for the night-rule fit check + the created booking.
const SESSION_DURATION_HOURS: Record<SelfBookSession, number> = {
  small: 1.5,
  'half-day': 4,
  'full-day': 8,
};

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

function hourOf(hhmm: string): number {
  return parseInt(hhmm.slice(0, 2), 10);
}

/** Next unique per-booking reference for a link, e.g. AL-LOCKE-4F7K-03. */
function mintBookingReference(link: SelfBookLink): string {
  const prefix = `${link.depositReference}-`;
  const used = Object.values(useBrainStore.getState().deposits).filter(
    (d) => d.depositReference?.startsWith(prefix),
  ).length;
  return `${prefix}${String(used + 1).padStart(2, '0')}`;
}

/**
 * Deterministic reconciliation code for an artist's universal link, e.g.
 * "AL-ASHER-4F7K": initials + surname + a stable 4-char code seeded from the
 * artist id so re-runs mint the same reference (deterministic, no Date.now).
 */
export function mintDepositReference(artist: Artist): string {
  const initials = `${artist.firstName[0] ?? ''}${artist.lastName[0] ?? ''}`.toUpperCase();
  const surname = artist.lastName.replace(/[^a-zA-Z]/g, '').toUpperCase() || artist.id.toUpperCase();
  let seed = 0;
  for (const ch of artist.id) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
  const rng = makePrng(seed);
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 4; i += 1) code += alphabet[Math.floor(rng() * alphabet.length)];
  return `${initials}-${surname}-${code}`;
}

/**
 * The artist's one universal, reusable self-book link. Customer-agnostic: no
 * customerId, no pinned session/deposit. Returns the existing link if the artist
 * already has one, else mints it (with a stable deposit reference).
 */
export function getOrCreateUniversalLink(opts: { artistId: string; studioId: StudioId }): SelfBookLink {
  const brain = useBrainStore.getState();
  const existing = Object.values(brain.selfBookLinks).find(
    (l) => l.universal && l.artistId === opts.artistId,
  );
  if (existing) return existing;

  const artist = brain.artists[opts.artistId];
  const now = new Date(nowIso());
  const oneYearOut = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString();
  return generateSelfBookLink({
    artistId: opts.artistId,
    studioId: opts.studioId,
    dateRangeStart: nowIso(),
    dateRangeEnd: oneYearOut,
    expiresAt: oneYearOut,
    channelSent: 'sms',
    universal: true,
    depositReference: artist ? mintDepositReference(artist) : `REF-${opts.artistId.toUpperCase()}`,
  });
}

export interface PinSlotResult {
  link: SelfBookLink;
  deposit: Deposit;
  booking: Booking;
}

/** Release a link's current live hold (if any) so a re-pin doesn't orphan it.
 *  Non-destructive: the superseded deposit becomes a findable 'expired' record,
 *  its held ghost booking is cancelled so the slot reopens. */
function releasePinnedHold(link: SelfBookLink): void {
  const brain = useBrainStore.getState();
  const priorId = link.pinnedDepositId;
  if (!priorId) return;
  const prior = brain.deposits[priorId];
  if (!prior || prior.status !== 'pending') return; // already paid/expired -> nothing to release
  brain.upsertDeposit({ ...prior, status: 'expired' });
  const bk = brain.bookings[`bk-${prior.id}`];
  if (bk && bk.status === 'held') {
    brain.upsertBooking({ ...bk, status: 'cancelled' });
  }
}

/**
 * Hold a slot for the artist's universal link: mint a pending deposit (unique
 * reconciliation ref) + a held ghost booking on that slot, and pin the slot onto
 * the link. Both records carry AWAITING_CLIENT until a client pays (see
 * completeUniversalSelfBook), then the real customer overwrites them.
 */
export function pinSelfBookSlot(opts: {
  link: SelfBookLink;
  session: SelfBookSession;
  startIso: string;
  /**
   * K4 custom hold: the artist set their own deposit + duration (half-hour steps).
   * `session` still tags the nearest bracket for downstream sessionLength consumers;
   * these values override the bracket's standard deposit amount and duration.
   */
  custom?: { depositAud: number; durationMin: number };
}): PinSlotResult {
  const brain = useBrainStore.getState();
  const { link, session, startIso, custom } = opts;
  releasePinnedHold(link);

  const deposit = generateDepositLink({
    customerId: AWAITING_CLIENT,
    studioId: link.studioId,
    artistId: link.artistId,
    amountAud: custom ? custom.depositAud : SESSION_DEPOSIT_AUD[session],
    channel: 'sms',
    sessionLength: session,
    source: 'artist-link',
    depositReference: mintBookingReference(link),
  });

  const bookingId = `bk-${deposit.id}`;
  const booking = {
    id: bookingId,
    customerId: AWAITING_CLIENT,
    studioId: link.studioId,
    artistId: link.artistId,
    start: startIso,
    durationMin: custom ? custom.durationMin : SESSION_DURATION_MIN[session],
    sessionLength: session,
    status: 'held' as const,
    depositId: deposit.id,
    designBrief: 'Held for a self-book link. Awaiting client.',
    referencePhotos: [],
    source: 'artist-link' as const,
    createdAt: nowIso(),
  };
  brain.upsertBooking(booking);

  const pinned: SelfBookLink = {
    ...link,
    pinnedStartIso: startIso,
    sessionLength: session,
    pinnedDepositId: deposit.id,
  };
  brain.upsertSelfBookLink(pinned);

  return { link: pinned, deposit, booking };
}

/** ISO start for a given calendar day + one of the fixed start times. */
export function slotStartIso(day: Date, start: SelfBookStart): string {
  const d = new Date(day);
  d.setHours(hourOf(start), 0, 0, 0);
  return d.toISOString();
}

/**
 * Which of the two fixed starts (11:00 / 15:00) the artist actually offers on
 * `day` for the chosen session. Applies:
 *  - day-off (pattern / leave) → no starts,
 *  - the night rule: a start is offered only when the session fits before the
 *    day's pattern end, OR the artist has opted into nightSessions,
 *  - first-to-pay-wins: a start already taken by a live booking is dropped, so a
 *    paid slot stops appearing on other open links (no pre-reserved slot).
 */
export function startOptionsForDay(
  artist: Artist,
  day: Date,
  session: SelfBookSession,
): SelfBookStart[] {
  if (resolveDayOff(artist.availability, day)) return [];

  const dowKey = (['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const)[day.getDay()]!;
  const window = artist.availability.pattern[dowKey];
  if (!window) return [];
  const patternEndHour = hourOf(window.endTime);
  const durationHours = SESSION_DURATION_HOURS[session];
  const nightSessions = artist.availability.nightSessions === true;

  const takenStarts = liveBookingStartHours(artist.id, day);

  return SELF_BOOK_STARTS.filter((start) => {
    const startHour = hourOf(start);
    if (startHour < hourOf(window.startTime)) return false;
    const fits = startHour + durationHours <= patternEndHour;
    if (!fits && !nightSessions) return false;
    return !takenStarts.has(startHour);
  });
}

// Start hours already claimed by a live (non-cancelled) booking for this artist
// on this day - the paid-slot contention set.
function liveBookingStartHours(artistId: string, day: Date): Set<number> {
  const brain = useBrainStore.getState();
  const key = format(day, 'yyyy-MM-dd');
  const taken = new Set<number>();
  for (const b of Object.values(brain.bookings)) {
    if (b.artistId !== artistId) continue;
    if (b.status === 'cancelled' || b.status === 'no-show' || b.status === 'superseded') continue;
    const start = new Date(b.start);
    if (format(start, 'yyyy-MM-dd') !== key) continue;
    taken.add(start.getHours());
  }
  return taken;
}

const NON_DIGIT = /\D/g;

/**
 * Match the client to an existing customer by any reliable identifier (mobile or
 * email), else by exact name, else create a fresh record. No merge logic - a hit
 * on any identifier reuses that card; no hit creates one.
 */
export function matchOrCreateCustomer(input: {
  name: string;
  email: string;
  mobile: string;
  studioId: StudioId;
}): string {
  const brain = useBrainStore.getState();
  const mobileDigits = input.mobile.replace(NON_DIGIT, '');
  const emailNorm = input.email.trim().toLowerCase();
  const nameNorm = input.name.trim().toLowerCase();

  const match = Object.values(brain.customers).find((c) => {
    if (mobileDigits && c.phone.replace(NON_DIGIT, '') === mobileDigits) return true;
    if (emailNorm && c.email?.trim().toLowerCase() === emailNorm) return true;
    const full = `${c.firstName} ${c.lastName}`.trim().toLowerCase();
    return nameNorm.length > 0 && full === nameNorm;
  });
  if (match) return match.id;

  const [firstName, ...rest] = input.name.trim().split(/\s+/);
  const id = `cust-artistlink-${mobileDigits || Date.now()}`;
  const customer: CustomerRecord = {
    id,
    firstName: firstName ?? input.name.trim(),
    lastName: rest.join(' '),
    phone: input.mobile.trim(),
    email: emailNorm || undefined,
    ageBand: '25-34',
    suburb: '',
    postcode: '',
    state: 'VIC',
    distanceToNearestStudioKm: 0,
    nearestStudioId: input.studioId,
    primaryStudioId: input.studioId,
    source: { channel: 'web-form', firstTouchAt: nowIso() },
    leadScore: 50,
    leadStage: 'deposit-pending',
    toneTag: 'casual-young',
    engagementStatus: 'active',
    conversation: [],
    rapportNotes: [],
    futureReadinessSignals: [],
    bookings: [],
    pieces: [],
    deposits: [],
    creditOnFile: { balanceAud: 0, history: [] },
    crossStudioTouches: [],
    doNotContact: false,
    lifetimeSpendAud: 0,
  };
  brain.upsertCustomer(customer);
  return id;
}

/** C6: notify the booking artist first, in a system→artist DM thread. */
export function notifyArtistOfSelfBook(opts: { artistId: string; customerId: string; body: string }): void {
  const brain = useBrainStore.getState();
  const threadId = brain.openOrCreateDm(['system-bot', opts.artistId], 'system-bot', nowIso());
  brain.sendDmMessage(threadId, opts.body, {
    authorId: 'system-bot',
    timestamp: nowIso(),
    customerRefId: opts.customerId,
  });
}

export interface CompleteSelfBookInput {
  link: SelfBookLink;
  session: SelfBookSession;
  startIso: string;
  details: { name: string; email: string; mobile: string };
  designBrief: string;
  referencePhotos: string[];
}

export interface CompleteSelfBookResult {
  customerId: string;
  depositId: string;
  bookingId: string;
  heldUntilIso: string;
}

/**
 * Payment completion for the universal link. Resolves the customer (match-or-
 * create). If the slot was already pinned by the artist (pinSelfBookSlot), a
 * held deposit + ghost booking already exist under AWAITING_CLIENT - attach the
 * resolved customer to that same deposit and confirm through the normal Stripe
 * webhook path, WITHOUT minting a second deposit. Otherwise (the open,
 * client-pick path) mint a fresh deposit stamped with the link's reference, then
 * run the NORMAL Stripe webhook path (simulateStripeWebhookPaid) so the booking,
 * confirmation cadence and waiver all fire exactly as a regular booking. Adds the
 * immediate confirmation text into the customer thread and notifies the artist
 * first (C6), then the shared studio channel.
 */
export function completeUniversalSelfBook(input: CompleteSelfBookInput): CompleteSelfBookResult {
  const brain = useBrainStore.getState();
  const { link } = input;

  // Identity resolved entirely from what the client entered (universal link has
  // no customerId); a staff-sent named link keeps its customer.
  const customerId =
    link.customerId ?? matchOrCreateCustomer({ ...input.details, studioId: link.studioId });

  // PINNED PATH: a held deposit + booking already exist. Attach the customer and
  // reuse the normal webhook path to confirm - do NOT mint a second deposit.
  // (`brain` is a point-in-time snapshot, so build the merged record directly
  // rather than re-reading it back out of `brain.deposits` after the upsert.)
  const pinned = link.pinnedDepositId ? brain.deposits[link.pinnedDepositId] : undefined;
  let deposit: Deposit;
  if (pinned) {
    deposit = { ...pinned, customerId };
    brain.upsertDeposit(deposit);
  } else {
    // Open, client-pick path (no pin): mint a fresh deposit carrying a UNIQUE
    // per-booking reference (C5). mintBookingReference suffixes the link's shared
    // code (e.g. AL-LOCKE-4F7K-03), so two clients booking the same length never
    // collide on the reconciliation key (reference::amount) in depositReconFlow.
    deposit = generateDepositLink({
      customerId,
      studioId: link.studioId,
      artistId: link.artistId,
      amountAud: SESSION_DEPOSIT_AUD[input.session],
      channel: 'sms',
      sessionLength: input.session,
      source: 'artist-link',
      depositReference: mintBookingReference(link),
    });
  }

  // Extend (not fork) the existing webhook path - it confirms the booking, marks
  // the deposit paid, and auto-sends the waiver on the normal cadence.
  simulateStripeWebhookPaid({
    depositId: deposit.id,
    bookingStartIso: input.startIso,
    designBrief: input.designBrief,
    referencePhotos: input.referencePhotos,
  });

  const bookingId = `bk-${deposit.id}`;
  // Universal hold window (15 Jul ruling): 24h inside 7 days, 48h outside.
  const heldUntilIso = holdExpiresAtIso(input.startIso, nowIso());
  const whenLabel = new Date(input.startIso).toLocaleString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const artist = brain.artists[link.artistId];
  const artistName = artist?.displayName ?? link.artistId;

  // Immediate confirmation text into the customer thread.
  brain.addConversationEntry(customerId, {
    id: `msg-artistlink-${deposit.id}`,
    customerId,
    channel: 'sms',
    direction: 'out',
    body: `You're booked in with ${artistName} for ${whenLabel}. Your deposit is paid. We'll send a reminder before the day.`,
    timestamp: nowIso(),
    authorRole: 'system',
  });

  brain.appendActivity({
    id: `act-artistlink-${deposit.id}`,
    type: 'booking-created',
    customerId,
    studioId: link.studioId,
    artistId: link.artistId,
    body: `New self-book via ${artistName}'s link (${deposit.depositReference}) for ${whenLabel}.`,
    timestamp: nowIso(),
  });

  // C6: alert the artist FIRST, in their own DM thread, then the shared channel.
  notifyArtistOfSelfBook({
    artistId: link.artistId,
    customerId,
    body: `📅 New self-book for ${whenLabel} · ${deposit.depositReference}. Deposit paid.`,
  });
  const target = autoPingChannelFor('deposit-paid', link.studioId, brain.internalComms);
  if (target) {
    brain.addAutoPing(
      target,
      `📅 ${artistName} just got a self-book for ${whenLabel} · ref ${deposit.depositReference}.`,
      { customerRefId: customerId, timestamp: nowIso() },
    );
  }

  // A universal link is reusable and stays active - clear any pin so the slot
  // stops being held (no pre-reserved slot once paid). A staff named link is
  // single-use - mark it paid.
  if (link.universal) {
    brain.upsertSelfBookLink({
      ...link,
      pinnedStartIso: undefined,
      sessionLength: undefined,
      pinnedDepositId: undefined,
    });
  } else {
    brain.upsertSelfBookLink({ ...link, status: 'paid', bookingId });
  }

  return { customerId, depositId: deposit.id, bookingId, heldUntilIso };
}

/**
 * Sim-clock reconciler for expired artist self-book links (tv-311, Diagram 05/09,
 * DECIDED 18 Aug). Mirrors the sibling reconcilers (reconcileDepositHolds,
 * reconcileTopupSchedule): "now" is the sim clock, never Date.now(); it loops
 * every link, skips settled records, and persists via upsertSelfBookLink.
 *
 * When a NAMED (staff-sent) link passes its `expiresAt`, mark it `expired` - a
 * read-only "expired" notification on the artist's profile; the artist never
 * chases - and mint an AGENT hot lead (fresh cohort) so the follow-up re-enters
 * warm on the Agent worklist, NOT the Sweeper's expired gap bucket. Universal
 * (customer-agnostic, reusable) links carry no client to follow up, so they are
 * left untouched. Idempotent: the `expired` status guard stops a re-mint on the
 * next pass. Expired DEPOSIT links are unaffected (routeExpiredResend owns them).
 */
export function reconcileSelfBookLinkExpiry(): void {
  const brain = useBrainStore.getState();
  const now = new Date(nowIso()).getTime();

  for (const link of Object.values(brain.selfBookLinks)) {
    if (link.status === 'expired' || link.status === 'paid') continue;
    // Universal links are reusable and customer-agnostic - no client, no hot lead.
    if (link.universal || !link.customerId) continue;
    if (new Date(link.expiresAt).getTime() >= now) continue;

    const customerId = link.customerId;
    // Read-only artist-profile 'expired' notification (the artist never chases).
    brain.upsertSelfBookLink({ ...link, status: 'expired' });

    // The follow-up is the AGENT's: setSequenceState with a sourceConversationId
    // mints a fresh pushed-lead card via buildStandardQueue (agent lane), so the
    // client re-enters warm - NOT a Sweeper gap item.
    const existing = brain.sequenceState[customerId];
    brain.setSequenceState(customerId, {
      ...existing,
      stage: 1,
      cohort: 'fresh',
      redKind: undefined,
      nextDueAt: undefined,
      sourceConversationId: existing?.sourceConversationId ?? customerId,
      description: 'Self-book link expired - follow up as a hot lead',
    });

    brain.appendActivity({
      id: `act-selfbook-expired-${link.token}`,
      type: 'pushed-to-queue',
      customerId,
      studioId: link.studioId,
      artistId: link.artistId,
      body: 'Self-book link expired. Artist notified (read-only); following up as an Agent hot lead.',
      timestamp: nowIso(),
    });

    eventBus.publish('self-book-link:expired', {
      token: link.token,
      customerId,
      artistId: link.artistId,
    });
  }
}
